"use client";

import { Fragment, JSX, ReactNode, useCallback, useMemo, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { SwipeScroll } from "@/components/charts/swipe-scroll";
import { useDismiss } from "@/lib/hooks/use-dismiss";
import { useHoverCapable } from "@/lib/hooks/use-hover-capable";
import { useHoverSelect } from "@/lib/hooks/use-hover-select";
import { useViewerLanguage } from "@/lib/i18n";
import { stripInlineMarkdown } from "@/utils/inline-markdown";
import { MODULE_LABEL_WRAP } from "./module-label-budget";
import { importsTokenScope } from "./imports-token-scope";
import { ModulePager, pageCountOf } from "./shared/module-pager";
import {
  FEATURE_COMPARISON_LABEL_COLUMN_WIDTH,
  FEATURE_COMPARISON_PLAN_COLUMN_WIDTH,
  FEATURE_COMPARISON_ROWS_PER_PAGE,
} from "@/config/layout";

export type FeatureComparisonValue = boolean | string;

export type FeatureComparisonRow = {
  name: string;
  values: FeatureComparisonValue[];
};

export type FeatureComparisonPlan = {
  name: string;
  price?: string;
  highlight?: boolean;
};

export type FeatureComparisonData = {
  features: FeatureComparisonRow[];
  plans: FeatureComparisonPlan[];
};

const DEFAULT_DATA: FeatureComparisonData = {
  features: [
    { name: "Team members", values: ["3", "10", "50", "Unlimited"] },
    { name: "Storage", values: ["1 GB", "10 GB", "500 GB", "5 TB"] },
    { name: "API calls / month", values: ["1,000", "50,000", "500,000", "Unlimited"] },
    { name: "SSO", values: [false, false, true, true] },
    { name: "Audit log", values: [false, false, true, true] },
    { name: "Priority support", values: [false, false, true, true] },
    { name: "Custom domains", values: [false, "1", "5", "Unlimited"] },
    { name: "Advanced analytics", values: [false, true, true, true] },
    { name: "SLA", values: [false, false, "99.5%", "99.99%"] },
  ],
  plans: [
    { name: "Free", price: "€0", highlight: false },
    { name: "Pro", price: "€29/mo", highlight: false },
    { name: "Business", price: "€99/mo", highlight: true },
    { name: "Enterprise", price: "Custom", highlight: false },
  ],
};

function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" className="inline-block" aria-hidden>
      <path
        d="M3 7 L6 10 L11 4"
        fill="none"
        stroke="var(--module-accent)"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function DashIcon() {
  return (
    <span className="text-[var(--subtle)] text-lg leading-none">−</span>
  );
}

function renderCell(val: FeatureComparisonValue): ReactNode {
  if (val === true) return <CheckIcon />;
  if (val === false) return <DashIcon />;
  return (
    <span
      title={val}
      className="line-clamp-2 text-sm tabular-nums text-[var(--foreground)] [overflow-wrap:anywhere]"
    >
      {val}
    </span>
  );
}

/** Which row and column the detail line is reading. Either half can stand alone. */
type FeatureComparisonCursor = { row: number | null; col: number | null };

export default function ModuleFeatureComparison({
  data = DEFAULT_DATA,
  grayscale,
  accent,
  className,
}: {
  data?: FeatureComparisonData;
  grayscale?: boolean;
  accent?: number;
  className?: string;
}): JSX.Element {
  const { t } = useViewerLanguage();
  const features = data.features ?? [];
  const plans = data.plans ?? [];

  const pageSize = FEATURE_COMPARISON_ROWS_PER_PAGE;
  const paged = features.length > pageSize;
  const slots = Math.max(1, Math.min(pageSize, features.length));
  const [page, setPage] = useState(0);
  const current = Math.min(page, pageCountOf(features.length, pageSize) - 1);
  const start = current * pageSize;
  const shown = useMemo(
    () => features.slice(start, start + pageSize),
    [features, start, pageSize],
  );
  const fillerCount = Math.max(0, slots - shown.length);

  const hasHover = useHoverCapable();
  const gridRef = useRef<HTMLDivElement>(null);
  const cursor = useHoverSelect<FeatureComparisonCursor>({
    eq: (a, b) => a.row === b.row && a.col === b.col,
  });
  const { setHovered, setSelected } = cursor;
  const activeRow = cursor.effective?.row ?? null;
  const activeCol = cursor.effective?.col ?? null;

  const clearSelection = useCallback(() => setSelected(null), [setSelected]);
  useDismiss(cursor.selected !== null, clearSelection, gridRef);

  const cellBg = (r: number, c: number): string | undefined => {
    const isRow = activeRow === r;
    const isCol = activeCol === c;
    if (isRow && isCol) return "bg-neutral-100 dark:bg-white/[0.08]";
    if (isRow || isCol) return "bg-neutral-50 dark:bg-white/[0.05]";
    if (plans[c]?.highlight) return "bg-neutral-50/60 dark:bg-white/[0.03]";
    return undefined;
  };

  const activeValue =
    activeRow !== null && activeCol !== null
      ? features[activeRow]?.values[activeCol]
      : undefined;

  return (
    <div className={cn("flex w-full flex-col gap-3", className)} style={importsTokenScope(grayscale, accent)}>
      <div className="h-5 truncate text-xs leading-5 text-[var(--muted-foreground)]">
        {activeRow !== null && activeCol !== null && activeValue !== undefined ? (
          <>
            <span className="mr-2 text-[10px] text-[var(--subtle)]">
              {t("Detail")}
            </span>
            <span
              className={cn(
                "font-medium text-[var(--foreground)]",
                MODULE_LABEL_WRAP.label,
              )}
            >
              {stripInlineMarkdown(features[activeRow]?.name)}
            </span>
            {" · "}
            <span className={MODULE_LABEL_WRAP.label}>
              {stripInlineMarkdown(plans[activeCol]?.name)}
            </span>
            {" · "}
            <span
              className={cn(
                "font-medium text-[var(--foreground)]",
                MODULE_LABEL_WRAP.label,
              )}
            >
              {activeValue === true
                ? t("Included")
                : activeValue === false
                  ? t("Not available")
                  : activeValue}
            </span>
          </>
        ) : (
          <span className="text-[var(--subtle)]">
            {t("Select a cell for feature × plan detail")}
          </span>
        )}
      </div>

      <SwipeScroll className="pb-2 [mask-image:linear-gradient(to_right,black_0%,black_93%,transparent_100%)] [-webkit-mask-image:linear-gradient(to_right,black_0%,black_93%,transparent_100%)]">
        <div
          ref={gridRef}
          className="grid w-max pr-10"
          style={{
            gridTemplateColumns: `${FEATURE_COMPARISON_LABEL_COLUMN_WIDTH}px repeat(${plans.length}, ${FEATURE_COMPARISON_PLAN_COLUMN_WIDTH}px)`,
          }}
        >
          <div className="border-b border-r border-dashed border-neutral-200 dark:border-white/[0.14] px-4 py-4" />

          {plans.map((plan, i) => (
            <div
              key={i}
              className={cn(
                "cursor-pointer border-b border-dashed border-neutral-200 dark:border-white/[0.14] px-4 py-4 text-center transition-colors",
                plan.highlight && "bg-neutral-50/60 dark:bg-white/[0.03]",
                activeCol === i && "bg-neutral-100 dark:bg-white/[0.08]",
              )}
              onMouseEnter={
                hasHover ? () => setHovered({ row: null, col: i }) : undefined
              }
              onMouseLeave={hasHover ? () => setHovered(null) : undefined}
              onClick={(e) =>
                cursor.select(
                  { row: null, col: i },
                  {
                    kind: "cell",
                    id: `col:${i}`,
                    index: i,
                    element: e.currentTarget,
                  },
                )
              }
            >
              <div
                className={cn(
                  "mx-auto text-[13px] font-medium",
                  MODULE_LABEL_WRAP.cell,
                  plan.highlight ? "text-[var(--foreground)]" : "text-[var(--muted-foreground)]",
                )}
              >
                {stripInlineMarkdown(plan.name)}
              </div>
              {plan.price && (
                <div
                  className={cn(
                    "mx-auto mt-1 text-[11px] text-[var(--subtle)]",
                    MODULE_LABEL_WRAP.cell,
                  )}
                >
                  {stripInlineMarkdown(plan.price)}
                </div>
              )}
            </div>
          ))}

          {shown.map((feat, i) => {
            const r = start + i;
            return (
              <Fragment key={r}>
                <div
                  data-module-type="feature-comparison"
                  data-part-index={`row:${r}`}
                  data-part-label={feat.name}
                  title={stripInlineMarkdown(feat.name)}
                  className={cn(
                    "flex h-13 cursor-pointer items-center border-b border-r border-dashed border-neutral-200 dark:border-white/[0.14] px-4 text-[13px] transition-colors",
                    activeRow === r
                      ? "bg-neutral-50 dark:bg-white/[0.05] font-medium text-[var(--foreground)]"
                      : "text-[var(--muted-foreground)]",
                  )}
                  onMouseEnter={
                    hasHover
                      ? () => setHovered({ row: r, col: null })
                      : undefined
                  }
                  onMouseLeave={hasHover ? () => setHovered(null) : undefined}
                  onClick={(e) =>
                    cursor.select(
                      { row: r, col: null },
                      {
                        kind: "row",
                        id: `row:${r}`,
                        index: r,
                        element: e.currentTarget,
                      },
                    )
                  }
                >
                  <span className="line-clamp-2 [overflow-wrap:anywhere]">
                    {stripInlineMarkdown(feat.name)}
                  </span>
                </div>
                {feat.values.map((val, c) => (
                  <div
                    key={c}
                    data-module-type="feature-comparison"
                    data-part-index={`${r}:${c}`}
                    data-part-label={`${feat.name} × ${plans[c]?.name ?? ""}`}
                    className={cn(
                      "flex h-13 cursor-pointer items-center justify-center border-b border-dashed border-neutral-200 dark:border-white/[0.14] px-4 text-center transition-colors",
                      cellBg(r, c),
                    )}
                    onMouseEnter={
                      hasHover
                        ? () => setHovered({ row: r, col: c })
                        : undefined
                    }
                    onMouseLeave={hasHover ? () => setHovered(null) : undefined}
                    onClick={(e) =>
                      cursor.select(
                        { row: r, col: c },
                        {
                          kind: "cell",
                          id: `${r}:${c}`,
                          index: r * plans.length + c,
                          element: e.currentTarget,
                        },
                      )
                    }
                  >
                    {renderCell(val)}
                  </div>
                ))}
              </Fragment>
            );
          })}

          {Array.from({ length: fillerCount }).map((_, i) => (
            <Fragment key={`filler-${i}`}>
              <div aria-hidden className="h-13 border-r border-dashed border-neutral-200 dark:border-white/[0.14]" />
              {plans.map((plan, c) => (
                <div
                  key={c}
                  aria-hidden
                  className={cn("h-13", plan.highlight && "bg-neutral-50/60 dark:bg-white/[0.03]")}
                />
              ))}
            </Fragment>
          ))}
        </div>
      </SwipeScroll>

      {paged && (
        <ModulePager
          page={current}
          pageSize={pageSize}
          total={features.length}
          onPageChange={setPage}
        />
      )}
    </div>
  );
}
